import * as THREE from "three";
import type { HighlightDataTexture } from "../../HighlightDataTexture";
import { DEFAULT_HATCH_THRESHOLD } from "../../materials/hatchTextureMaterial";
import { TONE_MAPPING_ALGORITHM } from "../../sceneLights";
import type { WebGLExtendedCapabilities } from "../../WebGLExtendedCapabilities";
import { AOAlgorithm } from "./AOAlgorithm";
import { defaultDepthNormalPassParams, type DepthNormalPassParams } from "./DepthNormalPass";
import type { DepthTextureType } from "./DepthTextureType";
import type { GTAOPassParams } from "./GTAOPass";
import { defaultGTAOPassParams } from "./GTAOPass";
import { defaultLightProbeGenerationParams, type LightProbeGenerationParams } from "./LightProbeGenerationParams";
import type { SoftShadowParams } from "./MaterialSharedDefines";
import { defaultSoftShadowParams } from "./MaterialSharedDefines";
import {
  MaterialSharedParameterGroup,
  PROBE_GRID_SMOOTH_BACKFACE_TERM_CONSTANT_TERM_DEFAULT,
  PROBE_GRID_NORMAL_OFFSET_MULTIPLIER_DEFAULT,
} from "./MaterialSharedParameterGroup";
import { RenderParamsSource } from "./RenderParamsSource";
import type { SAOPass2Params } from "./SAOPass2";
import { defaultSAOPass2Params } from "./SAOPass2";
import { defaultScreenSpaceShadowParams, type ScreenSpaceShadowParams } from "./ScreenSpaceShadowPass";
import { LightProbeVisualizationMode } from "./SHLightProbeConstants";
import type { DOFParams } from "./SSAAPass";
import { defaultDOFParams } from "./SSAAPass";
import { SSFXRendererOutput } from "./SSFXRendererOutput";
import type SSFXRendererScene from "./SSFXRendererScene";
import type {
  RenderPipelineParams,
  LightProbeRenderParams,
  LineworkParams,
  RendererParamOverrides,
} from "./SSFXRendererTypes";
import type { ReflectionParams } from "./SSRRPass";
import { defaultReflectionParams } from "./SSRRPass";

/**
 * Interface for render targets that contain depth/AO passes.
 * Used by RenderPipelineParamsContainer to update pass parameters.
 */
export interface RenderTargetPassAccess {
  depthNormalPass: {
    params: { downsampleDepthSteps: number };
    depthTextureType: DepthTextureType;
  };
  saoPass: { depthTextureType: DepthTextureType };
  gtaoPass: { depthTextureType: DepthTextureType };
}

export interface ScreenRenderTargetParams {
  ssaaTemporalSampleLevel: number;
  renderResolutionMultiplier: number;
  reprojectionTAAEnabled: boolean;
}

export const defaultRenderPipelineParams: RenderPipelineParams = {
  output: SSFXRendererOutput.FINAL_RENDER_WITH_EFFECTS,
  transparentBackground: false,
  useHalfFloatLinearRenderBuffer: false, // Note that if we want to enable this by default in the future, we'll still have to respect the supportsHalfFloatRenderTargetAndLinearSampling flag.
  toneMapping: TONE_MAPPING_ALGORITHM,
  includeOverlayInOutput: true,
  aoAlgorithm: AOAlgorithm.GTAO,
  reflectionsEnabled: false,
  screenSpaceShadowsEnabled: true,
  smallScaleAOEnabled: true,
  substitutedReflectionMultiplier: 0.25,
  isolateModeSupported: true,
  downsampleDepthSteps: 0,
  depthTextureType: 0, // Note that this param is set at initialization time depending on WebGL support.
};

export const defaultLightProbeRenderParams: LightProbeRenderParams = {
  lightProbesEnabled: true,
  lightProbeVisualization: LightProbeVisualizationMode.None,
  lightProbeIrradianceMultiplier: 1.02,
  probeGridNormalOffsetMultiplier: PROBE_GRID_NORMAL_OFFSET_MULTIPLIER_DEFAULT,
  probeGridSmoothBackfaceWeightConstantTerm: PROBE_GRID_SMOOTH_BACKFACE_TERM_CONSTANT_TERM_DEFAULT,
};

export const defaultLineworkParams: LineworkParams = {
  lineworkNormalAngleThreshold: 0.09 * Math.PI,
  // A depth threshold of zero means that the epsilon value specific to the depth buffer precision will be used.
  lineworkDepthThreshold: 0,
  hatchMaterialsEnabled: true,
  hatchDistanceThreshold: DEFAULT_HATCH_THRESHOLD,
};

export function applyMaterialSharedParameterGroupDefines(softShadowParams: SoftShadowParams) {
  MaterialSharedParameterGroup.instance.sharedDefines.useAdvancedPCSS = softShadowParams.useAdvancedPCSS;
  MaterialSharedParameterGroup.instance.sharedDefines.useSmoothstepShadowComparison =
    softShadowParams.useSmoothstepShadowComparison;
  MaterialSharedParameterGroup.instance.sharedDefines.pcssPenumbraAffectedByDistance =
    softShadowParams.pcssPenumbraAffectedByDistance;
  MaterialSharedParameterGroup.instance.sharedDefines.pcssNumSamples = softShadowParams.pcssNumSamples;
}

export interface RendererParams {
  renderPipelineParams: RenderPipelineParams;
  lineworkParams: LineworkParams;
  depthNormalParams: DepthNormalPassParams;
  saoParams: SAOPass2Params;
  gtaoParams: GTAOPassParams;
  dofParams: DOFParams;
  reflectionParams: ReflectionParams;
  screenSpaceShadowParams: ScreenSpaceShadowParams;
  lightProbeRenderParams: LightProbeRenderParams;
  softShadowParams: SoftShadowParams;
}

export function defaultRendererParams(capabilities: WebGLExtendedCapabilities): RendererParams {
  const defaults: RendererParams = {
    renderPipelineParams: { ...defaultRenderPipelineParams, depthTextureType: capabilities.defaultDepthTextureType },
    lineworkParams: defaultLineworkParams,
    depthNormalParams: {
      ...defaultDepthNormalPassParams,
      enableViewZConversion: capabilities.defaultEnableViewZConversion,
    },
    saoParams: defaultSAOPass2Params,
    gtaoParams: defaultGTAOPassParams,
    dofParams: defaultDOFParams,
    reflectionParams: defaultReflectionParams,
    screenSpaceShadowParams: defaultScreenSpaceShadowParams,
    lightProbeRenderParams: defaultLightProbeRenderParams,
    softShadowParams: defaultSoftShadowParams,
  };
  return defaults;
}

export function combineRendererParams(
  params: RendererParams,
  overrides: Omit<RendererParamOverrides, "id" | "source">,
): RendererParams {
  const result: RendererParams = { ...params };
  for (const key of Object.keys(params)) {
    result[key] = overrides[key] ? { ...params[key], ...overrides[key] } : params[key];
  }
  return result;
}

export function combineOnScreenRendererParams(
  params: OnScreenRendererParams,
  overrides: Omit<OnScreenRendererParamOverrides, "id" | "source">,
): OnScreenRendererParams {
  const result: OnScreenRendererParams = { ...params };
  for (const key of Object.keys(params)) {
    result[key] = overrides[key] ? { ...params[key], ...overrides[key] } : params[key];
  }
  return result;
}

export interface ScreenRenderParams extends ScreenRenderTargetParams {
  ssaaEnabled: boolean;
  ssaaSamplesPerFrame: number;
  useLightweightPipeline: boolean;
  sectionTransformControlsEnabled: boolean;
  sectionTransformControlsMode: "translate" | "rotate";
}

export interface OnScreenRendererParams extends RendererParams {
  screenRenderParams: ScreenRenderParams;
}

export interface OnScreenRendererParamOverrides extends RendererParamOverrides {
  screenRenderParams?: Partial<ScreenRenderParams>;
}

export interface DefaultRenderParamOverrides extends Omit<OnScreenRendererParamOverrides, "id" | "source"> {
  depthNormalParams?: Partial<DepthNormalPassParams>;
}

export interface RenderImageOptions {
  width: number;
  height: number;
  ssaaSamples?: number;
  updateLightProbesBeforeCapture?: boolean;

  highlightDataTexture?: HighlightDataTexture;
}

export function renderParamsStackHash(stack: OnScreenRendererParamOverrides[]) {
  let hash = "";
  for (const params of stack) {
    hash += "_" + params?.id;
  }
  return hash;
}

export interface CameraGUIParams {
  near: number;
  far: number;
}

export type DatGuiArg =
  | number
  | string[]
  | number[]
  | { [visibleName: string]: number }
  | { [visibleName: string]: string };

export interface ParamOptions {
  disableGUI?: boolean;
  step?: number;
}

type ParamValueType = number | string | boolean | THREE.Layers;

interface Param {
  name: string;
  value: ParamValueType;
  options: ParamOptions;
  guiArg0?: DatGuiArg;
  guiArg1?: DatGuiArg;
}

export class ParamsContainer<ParamsType> {
  private paramsInAddOrder: Param[] = [];

  // Setting parameters through this interface will change the defaults used for rendering. SSFXRendererScene may override the values.
  params: ParamsType;

  constructor(private readonly markRendererForUpdate: () => void) {
    this.params = {} as any;
  }

  /**
   * @param renderTarget Will be used to fetch the initial value of the param, and nothing else.
   * @param name Parameter name/key.
   */
  addRenderTargetParam<T extends ParamValueType>(
    name: string,
    defaultValue: T,
    options: ParamOptions,
    guiArg0?: DatGuiArg,
    guiArg1?: DatGuiArg,
  ) {
    const param = {
      name: name,
      value: defaultValue,
      options: options,
      guiArg0: guiArg0,
      guiArg1: guiArg1,
    };

    this.paramsInAddOrder.push(param);

    Object.defineProperty(this.params, name, {
      get: () => {
        return param.value;
      },
      set: (value) => {
        param.value = value;
        this.markRendererForUpdate();
      },
      enumerable: true,
    });
  }

  addToDatGUI(guiFolder) {
    for (let paramIndex = 0; paramIndex < this.paramsInAddOrder.length; ++paramIndex) {
      const param = this.paramsInAddOrder[paramIndex];
      const options = param.options;
      if (options.disableGUI) {
        continue;
      }
      let added = guiFolder.add(this.params, param.name, param.guiArg0, param.guiArg1);
      if (options.step !== undefined) {
        added = added.step(options.step);
      }
      added = added.listen();
    }
  }
}

export class SSFXRendererParamsManager {
  private newParamsContainer<ParamsType>() {
    return new ParamsContainer<ParamsType>(() => this.markRendererForUpdate());
  }

  private renderPipelineParamsInternal = this.newParamsContainer<RenderPipelineParams>();
  private depthNormalParamsInternal = this.newParamsContainer<DepthNormalPassParams>();
  private saoParamsInternal = this.newParamsContainer<SAOPass2Params>();
  private gtaoParamsInternal = this.newParamsContainer<GTAOPassParams>();
  private dofParamsInternal = this.newParamsContainer<DOFParams>();
  private reflectionParamsInternal = this.newParamsContainer<ReflectionParams>();
  private screenSpaceShadowParamsInternal = this.newParamsContainer<ScreenSpaceShadowParams>();
  private lightProbeRenderParamsInternal = this.newParamsContainer<LightProbeRenderParams>();
  private lineworkParamsInternal = this.newParamsContainer<LineworkParams>();
  private softShadowParamsInternal = this.newParamsContainer<SoftShadowParams>();

  private renderPipelineParams = defaultRenderPipelineParams;
  readonly screenRenderParams: ScreenRenderParams;
  private depthNormalParams = defaultDepthNormalPassParams;
  private saoParams = defaultSAOPass2Params;
  private gtaoParams = defaultGTAOPassParams;
  private dofParams = defaultDOFParams;
  private reflectionParams = defaultReflectionParams;
  private screenSpaceShadowParams = defaultScreenSpaceShadowParams;
  lightProbeGenerationParams: LightProbeGenerationParams;
  lightProbeRenderParams = defaultLightProbeRenderParams;
  private lineworkParams = defaultLineworkParams;
  private softShadowParams = defaultSoftShadowParams;

  private readonly cameraParams: CameraGUIParams;

  private cameraNearGUI;
  private cameraFarGUI;

  private initializedGUI: boolean = false;

  private defaultRenderParams: OnScreenRendererParams;
  private renderParamsStack: OnScreenRendererParamOverrides[] = [];
  private lastRenderParamsStackHash: string = "";

  // Tracks scene/camera rendered on-screen.
  private lastRenderedScene: SSFXRendererScene | undefined;
  private lastRenderedCamera: THREE.Camera | undefined;

  constructor(
    private readonly markRendererForUpdate: () => void,
    capabilities: WebGLExtendedCapabilities,
    worldScale: number,
    defaultRenderParamOverrides,
  ) {
    this.lightProbeGenerationParams = defaultLightProbeGenerationParams;

    // This structure contains parameters that are specific to on-screen rendering, and not really relevant to offscreen rendering.
    // Number of SSAA samples for offscreen rendering is fixed, and resolution multiplier is never applied to offscreen rendering.
    this.screenRenderParams = {
      ssaaEnabled: true,
      ssaaSamplesPerFrame: 1,
      ssaaTemporalSampleLevel: 4,
      renderResolutionMultiplier: 1.0,
      reprojectionTAAEnabled: true,
      useLightweightPipeline: false,
      sectionTransformControlsEnabled: false,
      sectionTransformControlsMode: "translate",
    };

    // We have this cache for camera near/far to enable adjusting camera from dat.gui for graphics debugging, even if the camera changes.
    this.cameraParams = {
      near: 1,
      far: 1000,
    };

    this.renderPipelineParams = this.renderPipelineParamsInternal.params;
    const addRenderPipelineParam = (
      name: keyof RenderPipelineParams,
      options: ParamOptions,
      guiArg0?: DatGuiArg,
      guiArg1?: DatGuiArg,
    ) => {
      const defaultValue =
        name === "depthTextureType" ? capabilities.defaultDepthTextureType : defaultRenderPipelineParams[name];
      this.renderPipelineParamsInternal.addRenderTargetParam(name, defaultValue, options, guiArg0, guiArg1);
    };

    addRenderPipelineParam(
      "output",
      {},
      {
        "Final render with effects": SSFXRendererOutput.FINAL_RENDER_WITH_EFFECTS,
        Light: SSFXRendererOutput.LIGHT,
        "Light without detail": SSFXRendererOutput.LIGHT_WITHOUT_DETAIL,
        "Light probe irradiance": SSFXRendererOutput.LIGHT_PROBE_IRRADIANCE,
        "Base color": SSFXRendererOutput.BASE_COLOR,
        "Linework and selection": SSFXRendererOutput.LINEWORK_WITH_SELECTION,
        Linework: SSFXRendererOutput.LINEWORK,
        "Linework (transparent)": SSFXRendererOutput.LINEWORK_TRANSPARENT,
        "Screen space shadows": SSFXRendererOutput.SCREEN_SPACE_SHADOWS,
        "Transmission opaque buffer": SSFXRendererOutput.TRANSMISSION_VISUALIZATION,
        "Material numerical id": SSFXRendererOutput.MATERIAL_ID,
        AO: SSFXRendererOutput.AO,
        "Normal for AO": SSFXRendererOutput.NORMAL_FOR_AO,
        Depth: SSFXRendererOutput.DEPTH,
        "Depth (mip 1)": SSFXRendererOutput.DEPTH_MIP_1,
        "Depth (mip 2)": SSFXRendererOutput.DEPTH_MIP_2,
        "Depth (mip 3)": SSFXRendererOutput.DEPTH_MIP_3,
        "Depth (mip 4)": SSFXRendererOutput.DEPTH_MIP_4,
        "Depth (mip 5)": SSFXRendererOutput.DEPTH_MIP_5,
        Normal: SSFXRendererOutput.NORMAL,
        "Picking ids": SSFXRendererOutput.PICKING_IDS,
        "UV Calibration": SSFXRendererOutput.UV_CALIBRATION,
        "Mesh id": SSFXRendererOutput.MESH_ID,
      },
    );

    addRenderPipelineParam("transparentBackground", {});

    addRenderPipelineParam(
      "toneMapping",
      {},
      {
        Disabled: THREE.NoToneMapping,
        "ACES Filmic": THREE.ACESFilmicToneMapping,
        Cineon: THREE.CineonToneMapping,
        Reinhard: THREE.ReinhardToneMapping,
        Linear: THREE.LinearToneMapping,
      },
    );

    addRenderPipelineParam("useHalfFloatLinearRenderBuffer", {});

    addRenderPipelineParam("isolateModeSupported", {});

    addRenderPipelineParam("includeOverlayInOutput", {});

    addRenderPipelineParam(
      "aoAlgorithm",
      {},
      {
        SAO: AOAlgorithm.SAO,
        GTAO: AOAlgorithm.GTAO,
        none: AOAlgorithm.NONE,
      },
    );

    addRenderPipelineParam("reflectionsEnabled", {});
    addRenderPipelineParam("substitutedReflectionMultiplier", {}, 0.0, 1.0);

    addRenderPipelineParam("screenSpaceShadowsEnabled", {});
    addRenderPipelineParam("smallScaleAOEnabled", {});

    const depthTextureTypes = {
      "RGBA Packed Depth": 0,
      "Unsigned short": THREE.UnsignedShortType,
      "Unsigned int": THREE.UnsignedIntType,
      Float: THREE.FloatType,
    };
    addRenderPipelineParam("downsampleDepthSteps", {}, [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]);
    addRenderPipelineParam("depthTextureType", {}, depthTextureTypes);

    this.lineworkParams = this.lineworkParamsInternal.params;
    const addLineworkParam = (
      name: keyof LineworkParams,
      options: ParamOptions,
      guiArg0?: DatGuiArg,
      guiArg1?: DatGuiArg,
    ) => {
      const defaultValue = defaultLineworkParams[name];
      this.lineworkParamsInternal.addRenderTargetParam(name, defaultValue, options, guiArg0, guiArg1);
    };
    addLineworkParam("lineworkNormalAngleThreshold", {}, 0.0, Math.PI);
    addLineworkParam("lineworkDepthThreshold", {}, 0.0, 10.0);
    addLineworkParam("hatchMaterialsEnabled", {});
    addLineworkParam("hatchDistanceThreshold", {});

    this.depthNormalParams = this.depthNormalParamsInternal.params;
    const addDepthNormalParam = (name: string, options: ParamOptions, guiArg0?: DatGuiArg, guiArg1?: DatGuiArg) => {
      const defaultValue = defaultDepthNormalPassParams[name];
      this.depthNormalParamsInternal.addRenderTargetParam(name, defaultValue, options, guiArg0, guiArg1);
    };

    addDepthNormalParam("depthNormalLayerMask", { disableGUI: true });

    this.saoParams = this.saoParamsInternal.params;
    const addSAOParam = (
      name: keyof SAOPass2Params,
      options: ParamOptions,
      guiArg0?: DatGuiArg,
      guiArg1?: DatGuiArg,
    ) => {
      const defaultValue = defaultSAOPass2Params[name];
      this.saoParamsInternal.addRenderTargetParam(name, defaultValue, options, guiArg0, guiArg1);
    };

    addSAOParam("aoResolutionMultiplier", {}, 0.25, 1.0);
    addSAOParam("saoBias", {}, -1, 1);
    addSAOParam("aoIntensity", {}, 0, 3);
    addSAOParam("saoScale", {}, 0, 2);
    addSAOParam("worldSpaceScale", {}, 0.05 * worldScale, 1.0 * worldScale);
    addSAOParam("maxScreenSpaceKernelRadius", {}, 0.01, 0.3);
    addSAOParam("saoMinResolution", {}, 0, 0.2 * worldScale);
    addSAOParam("assumeThickGeometry", {});
    addSAOParam("assumedThickness", {}, 0, 10.0 * worldScale);
    addSAOParam("simplifiedSamplingRegion", {});

    addSAOParam("useNormalTextureInSAO", {});
    addSAOParam("ssaoBlur", {});
    addSAOParam("usePoissonBlur", {});
    addSAOParam("blurNormalCutoffEnabled", {});
    addSAOParam("depthCutoffBasedOnNormal", {});
    addSAOParam("blurKernelSamples", { step: 1 }, 0, 100);
    addSAOParam("blurStdDev", {}, 0.5, 150);

    addSAOParam("blurDepthCutoff", {}, 0.05 * worldScale, 1.0 * worldScale);
    addSAOParam("blurNormalAngleCutoff", {}, 0.0, Math.PI);
    addSAOParam("numSamples", { step: 1 }, 2, 31);

    this.gtaoParams = this.gtaoParamsInternal.params;
    const addGTAOParam = (
      name: keyof GTAOPassParams,
      options: ParamOptions,
      guiArg0?: DatGuiArg,
      guiArg1?: DatGuiArg,
    ) => {
      const defaultValue = defaultGTAOPassParams[name];
      this.gtaoParamsInternal.addRenderTargetParam(name, defaultValue, options, guiArg0, guiArg1);
    };

    addGTAOParam("aoResolutionMultiplier", {}, 0.25, 1.0);
    addGTAOParam("worldSpaceScale", {}, 0.2 * worldScale, 2.0 * worldScale);
    addGTAOParam("aoIntensity", {}, 0, 1);
    addGTAOParam("maxScreenSpaceKernelRadius", {}, 0.01, 0.3);
    addGTAOParam("falloffMinRelativeRadius", {}, 0.0, 2.0);
    addGTAOParam("falloffMaxRelativeRadius", {}, 0.0, 10.0);
    addGTAOParam("lowerIntensityOnVerticalSurfaces", {}, 0.0, 1.0);
    addGTAOParam("cosineWeighting", {});
    addGTAOParam("useNormalTextureInGTAO", {});
    addGTAOParam("ssaoBlur", {});
    addGTAOParam("usePoissonBlur", {});
    addGTAOParam("blurNormalCutoffEnabled", {});
    addGTAOParam("depthCutoffBasedOnNormal", {});
    addGTAOParam("blurKernelSamples", { step: 1 }, 0, 100);
    addGTAOParam("blurStdDev", {}, 0.5, 150);
    addGTAOParam("blurDepthCutoff", {}, 0.05 * worldScale, 1.0 * worldScale);
    addGTAOParam("blurNormalAngleCutoff", {}, 0.0, Math.PI);
    addGTAOParam("numAngleSamples", { step: 1 }, 1, 4);
    addGTAOParam("numSamples", { step: 1 }, 4, 50);

    this.dofParams = this.dofParamsInternal.params;
    const addDOFParam = (name: keyof DOFParams, options: ParamOptions, guiArg0?: DatGuiArg, guiArg1?: DatGuiArg) => {
      const defaultValue = defaultDOFParams[name];
      this.dofParamsInternal.addRenderTargetParam(name, defaultValue, options, guiArg0, guiArg1);
    };

    addDOFParam("applyDepthOfField", {});
    addDOFParam("apertureRadius", {}, 0.001 * worldScale, 0.5 * worldScale);
    addDOFParam("cameraFocusDistance", {}, 0.5 * worldScale, 30.0 * worldScale);

    this.reflectionParams = this.reflectionParamsInternal.params;
    const addSSRRParam = (
      name: keyof ReflectionParams,
      options: ParamOptions,
      guiArg0?: DatGuiArg,
      guiArg1?: DatGuiArg,
    ) => {
      const defaultValue = defaultReflectionParams[name];
      this.reflectionParamsInternal.addRenderTargetParam(name, defaultValue, options, guiArg0, guiArg1);
    };
    addSSRRParam("steps", { step: 1 }, 1, 50);
    addSSRRParam("stride", { step: 1 }, 1, 40);
    addSSRRParam("fineGrainedStride", { step: 1 }, 1, 40);
    addSSRRParam("jitter", {}, 0, 1);
    addSSRRParam("assumedThickness", {}, 0.01 * worldScale, 1.0 * worldScale);

    this.screenSpaceShadowParams = this.screenSpaceShadowParamsInternal.params;
    const addScreenSpaceShadowParam = (
      name: keyof ScreenSpaceShadowParams,
      options: ParamOptions,
      guiArg0?: DatGuiArg,
      guiArg1?: DatGuiArg,
    ) => {
      const defaultValue = defaultScreenSpaceShadowParams[name];
      this.screenSpaceShadowParamsInternal.addRenderTargetParam(name, defaultValue, options, guiArg0, guiArg1);
    };
    addScreenSpaceShadowParam("shadowSteps", { step: 1 }, 1, 10);
    addScreenSpaceShadowParam("assumedThickness", {}, 0.01 * worldScale, 1.0 * worldScale);
    addScreenSpaceShadowParam("aoSamples", { step: 1 }, 1, 6);
    addScreenSpaceShadowParam("aoAngleSamples", { step: 1 }, 1, 3);
    addScreenSpaceShadowParam("aoCosineWeighting", {});
    addScreenSpaceShadowParam("aoIntensity", {}, 0, 1);

    this.lightProbeRenderParams = this.lightProbeRenderParamsInternal.params;
    const addLightProbeRenderParam = (
      name: keyof LightProbeRenderParams,
      options: ParamOptions,
      guiArg0?: DatGuiArg,
      guiArg1?: DatGuiArg,
    ) => {
      const defaultValue = defaultLightProbeRenderParams[name];
      this.lightProbeRenderParamsInternal.addRenderTargetParam(name, defaultValue, options, guiArg0, guiArg1);
    };
    addLightProbeRenderParam(
      "lightProbeVisualization",
      {},
      {
        None: LightProbeVisualizationMode.None,
        Irradiance: LightProbeVisualizationMode.Irradiance,
        "Irradiance (spherical harmonics)": LightProbeVisualizationMode.IrradianceSH,
        Radiance: LightProbeVisualizationMode.Radiance,
        "Skip render": LightProbeVisualizationMode.SkipRender,
        "PMREM (frosted glass)": LightProbeVisualizationMode.PMREM,
      },
    );
    addLightProbeRenderParam("lightProbeIrradianceMultiplier", { step: 0.01 }, 0.0, 2.0);
    addLightProbeRenderParam("probeGridNormalOffsetMultiplier", {}, 0.0, 1.0);
    addLightProbeRenderParam("probeGridSmoothBackfaceWeightConstantTerm", {}, 0.0, 5.0);

    this.softShadowParams = this.softShadowParamsInternal.params;
    const addSoftShadowParam = (
      name: keyof SoftShadowParams,
      options: ParamOptions,
      guiArg0?: DatGuiArg,
      guiArg1?: DatGuiArg,
    ) => {
      const defaultValue = defaultSoftShadowParams[name];
      this.softShadowParamsInternal.addRenderTargetParam(name, defaultValue, options, guiArg0, guiArg1);
    };
    addSoftShadowParam("useAdvancedPCSS", {});
    addSoftShadowParam("useSmoothstepShadowComparison", {});
    addSoftShadowParam("pcssPenumbraAffectedByDistance", {});
    addSoftShadowParam("pcssNumSamples", { step: 1 }, 3, 23);

    this.defaultRenderParams = {
      screenRenderParams: { ...this.screenRenderParams },
      renderPipelineParams: { ...this.renderPipelineParams },
      lineworkParams: { ...this.lineworkParams },
      depthNormalParams: { ...this.depthNormalParams },
      saoParams: { ...this.saoParams },
      gtaoParams: { ...this.gtaoParams },
      dofParams: { ...this.dofParams },
      reflectionParams: { ...this.reflectionParams },
      screenSpaceShadowParams: { ...this.screenSpaceShadowParams },
      lightProbeRenderParams: { ...this.lightProbeRenderParams },
      softShadowParams: { ...this.softShadowParams },
    };

    if (defaultRenderParamOverrides) {
      this.defaultRenderParams = combineOnScreenRendererParams(this.defaultRenderParams, defaultRenderParamOverrides);
    }
  }

  getRenderParams(): RendererParams {
    return {
      renderPipelineParams: { ...this.renderPipelineParams },
      lineworkParams: { ...this.lineworkParams },
      depthNormalParams: { ...this.depthNormalParams },
      saoParams: { ...this.saoParams },
      gtaoParams: { ...this.gtaoParams },
      dofParams: { ...this.dofParams },
      reflectionParams: { ...this.reflectionParams },
      screenSpaceShadowParams: { ...this.screenSpaceShadowParams },
      lightProbeRenderParams: { ...this.lightProbeRenderParams },
      softShadowParams: { ...this.softShadowParams },
    };
  }

  setRenderParameters(options: OnScreenRendererParamOverrides) {
    const sourceNum: number = options.source;
    this.renderParamsStack[sourceNum] = options;
  }

  setupParamsForRender(scene: SSFXRendererScene, camera: THREE.Camera) {
    const sceneChanged = this.lastRenderedScene !== scene;
    this.lastRenderedScene = scene;
    this.lastRenderedCamera = camera;

    this.cameraParams["near"] = (camera as THREE.PerspectiveCamera).near;
    this.cameraParams["far"] = (camera as THREE.PerspectiveCamera).far;
    if (this.initializedGUI) {
      this.cameraNearGUI.updateDisplay();
      this.cameraFarGUI.updateDisplay();
    }

    const renderParamsStack: OnScreenRendererParamOverrides[] = [...this.renderParamsStack];
    renderParamsStack[RenderParamsSource.SCENE] = scene.getRenderParams();
    const hash = renderParamsStackHash(renderParamsStack);
    if (sceneChanged || hash !== this.lastRenderParamsStackHash) {
      // If something has changed in the render params stack, re-evaluate the render params. This will refresh values shown in dat.gui.
      // Dat.gui can still change these values afterwards, that's why we don't simply refresh them on every frame.
      this.lastRenderParamsStackHash = hash;
      let combinedRenderParams: OnScreenRendererParams = {
        screenRenderParams: { ...this.defaultRenderParams.screenRenderParams },
        renderPipelineParams: { ...this.defaultRenderParams.renderPipelineParams },
        lineworkParams: { ...this.defaultRenderParams.lineworkParams },
        depthNormalParams: { ...this.defaultRenderParams.depthNormalParams },
        saoParams: { ...this.defaultRenderParams.saoParams },
        gtaoParams: { ...this.defaultRenderParams.gtaoParams },
        dofParams: { ...this.defaultRenderParams.dofParams },
        reflectionParams: { ...this.defaultRenderParams.reflectionParams },
        screenSpaceShadowParams: { ...this.defaultRenderParams.screenSpaceShadowParams },
        lightProbeRenderParams: { ...this.defaultRenderParams.lightProbeRenderParams },
        softShadowParams: { ...this.defaultRenderParams.softShadowParams },
      };
      for (const overrides of renderParamsStack) {
        if (overrides) {
          combinedRenderParams = combineOnScreenRendererParams(combinedRenderParams, overrides);
        }
      }
      for (const key in combinedRenderParams.screenRenderParams) {
        this.screenRenderParams[key] = combinedRenderParams.screenRenderParams[key];
      }
      for (const key in combinedRenderParams.renderPipelineParams) {
        this.renderPipelineParams[key] = combinedRenderParams.renderPipelineParams[key];
      }
      for (const key in combinedRenderParams.lineworkParams) {
        this.lineworkParams[key] = combinedRenderParams.lineworkParams[key];
      }
      for (const key in combinedRenderParams.depthNormalParams) {
        this.depthNormalParams[key] = combinedRenderParams.depthNormalParams[key];
      }
      for (const key in combinedRenderParams.saoParams) {
        this.saoParams[key] = combinedRenderParams.saoParams[key];
      }
      for (const key in combinedRenderParams.gtaoParams) {
        this.gtaoParams[key] = combinedRenderParams.gtaoParams[key];
      }
      for (const key in combinedRenderParams.dofParams) {
        this.dofParams[key] = combinedRenderParams.dofParams[key];
      }
      for (const key in combinedRenderParams.reflectionParams) {
        this.reflectionParams[key] = combinedRenderParams.reflectionParams[key];
      }
      for (const key in combinedRenderParams.screenSpaceShadowParams) {
        this.screenSpaceShadowParams[key] = combinedRenderParams.screenSpaceShadowParams[key];
      }
      for (const key in combinedRenderParams.lightProbeRenderParams) {
        this.lightProbeRenderParams[key] = combinedRenderParams.lightProbeRenderParams[key];
      }
      for (const key in combinedRenderParams.softShadowParams) {
        this.softShadowParams[key] = combinedRenderParams.softShadowParams[key];
      }
    }
  }

  initGUI(gui: dat.GUI) {
    this.initializedGUI = true;

    const outputGUI = gui.addFolder("Render pipeline");

    const markRendererForUpdate = () => {
      this.markRendererForUpdate?.();
    };

    outputGUI.add(MaterialSharedParameterGroup.instance.sharedDefines, "wireframe").onChange(markRendererForUpdate);

    outputGUI.add(this.screenRenderParams, "useLightweightPipeline").onChange(markRendererForUpdate).listen();
    outputGUI.add(this.screenRenderParams, "ssaaEnabled").onChange(markRendererForUpdate).listen();
    outputGUI.add(this.screenRenderParams, "reprojectionTAAEnabled").onChange(markRendererForUpdate).listen();
    outputGUI
      .add(this.screenRenderParams, "ssaaSamplesPerFrame", 1, 8)
      .step(1)
      .onChange(markRendererForUpdate)
      .listen();
    outputGUI
      .add(this.screenRenderParams, "ssaaTemporalSampleLevel", 0, 8)
      .step(1)
      .onChange(markRendererForUpdate)
      .listen();
    outputGUI
      .add(this.screenRenderParams, "renderResolutionMultiplier", {
        "50%": 0.5,
        "100%": 1.0,
        "200%": 2.0,
      })
      .onChange(markRendererForUpdate)
      .listen();
    outputGUI.add(this.screenRenderParams, "sectionTransformControlsEnabled").onChange(markRendererForUpdate).listen();
    outputGUI
      .add(this.screenRenderParams, "sectionTransformControlsMode", ["translate", "rotate"])
      .onChange(markRendererForUpdate)
      .listen();
    this.renderPipelineParamsInternal.addToDatGUI(outputGUI);

    this.cameraNearGUI = outputGUI.add(this.cameraParams, "near", 0.4, 120.0).onChange(() => {
      if (this.lastRenderedCamera && this.lastRenderedCamera instanceof THREE.PerspectiveCamera) {
        this.lastRenderedCamera.near = this.cameraParams["near"];
        this.lastRenderedCamera.updateProjectionMatrix();
        markRendererForUpdate();
      }
    });
    this.cameraFarGUI = outputGUI.add(this.cameraParams, "far", 120.0, 40000.0).onChange(() => {
      if (this.lastRenderedCamera && this.lastRenderedCamera instanceof THREE.PerspectiveCamera) {
        this.lastRenderedCamera.far = this.cameraParams["far"];
        this.lastRenderedCamera.updateProjectionMatrix();
        markRendererForUpdate();
      }
    });

    const lineworkGUI = gui.addFolder("Linework");
    this.lineworkParamsInternal.addToDatGUI(lineworkGUI);

    const saoGUI = gui.addFolder("SAO");
    this.saoParamsInternal.addToDatGUI(saoGUI);

    const gtaoGUI = gui.addFolder("GTAO");
    this.gtaoParamsInternal.addToDatGUI(gtaoGUI);

    const probeGenGUI = gui.addFolder("Light Probe Generation");
    probeGenGUI.add(
      { updateAllLightProbes: () => this.lastRenderedScene?.dirtyAllLightProbes() },
      "updateAllLightProbes",
    );
    probeGenGUI
      .add(this.lightProbeGenerationParams, "lightProbeBounces", 0, 3)
      .step(1)
      .onChange(() => this.lastRenderedScene?.dirtyAllLightProbes());

    const probeGUI = gui.addFolder("Light Probe Rendering");
    probeGUI.add(MaterialSharedParameterGroup.instance.sharedDefines, "lightProbesEnabled");
    this.lightProbeRenderParamsInternal.addToDatGUI(probeGUI);

    const reflectGUI = gui.addFolder("Reflections");
    this.reflectionParamsInternal.addToDatGUI(reflectGUI);
    reflectGUI
      .add(MaterialSharedParameterGroup.instance.sharedDefines, "subMissingReflectionWithIrradiance")
      .onChange(markRendererForUpdate);

    const screenSpaceShadowsGUI = gui.addFolder("Screen space shadows");
    this.screenSpaceShadowParamsInternal.addToDatGUI(screenSpaceShadowsGUI);

    const dofGUI = gui.addFolder("Depth of Field");
    this.dofParamsInternal.addToDatGUI(dofGUI);

    const shadowGUI = gui.addFolder("Soft Shadows");
    this.softShadowParamsInternal.addToDatGUI(shadowGUI);

    const designMaterialGUI = gui.addFolder("Design Material Quality");
    designMaterialGUI
      .add(MaterialSharedParameterGroup.instance.sharedDefines, "parallaxEffectEnabled")
      .onChange(markRendererForUpdate);
  }
}
